import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import cn from "../lib/cn";

/** Page header used by inner pages (legal, contact, ...). */
const PageHero = ({
  badge,
  title,
  description,
  children,
  footer,
  className,
}: {
  badge: { icon: LucideIcon; label: string };
  title: string;
  description: ReactNode;
  children?: ReactNode;
  /** Pinned to the bottom border, e.g. tabs. */
  footer?: ReactNode;
  /** Overrides the container width (default max-w-5xl). */
  className?: string;
}) => {
  const BadgeIcon = badge.icon;
  return (
    <header className="relative overflow-hidden border-b border-gray-200 bg-white">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[420px] w-[620px] rounded-full bg-theme-primary/[0.12] blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 top-10 h-[300px] w-[420px] rounded-full bg-theme-primary/[0.06] blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          "relative mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8",
          footer && "!pb-0",
          className,
        )}
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-theme-primary/10 px-2.5 py-1 text-xs font-medium text-theme-primary ring-1 ring-inset ring-theme-primary/20">
          <BadgeIcon className="h-3.5 w-3.5" strokeWidth={2} />
          {badge.label}
        </span>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {title}
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-7 text-gray-500">
          {description}
        </p>
        {children && <div className="mt-6">{children}</div>}
      </motion.div>

      {/* Outside the entrance animation so tabs stay put between pages. */}
      {footer && (
        <div
          className={cn(
            "relative mx-auto max-w-5xl px-4 pt-10 sm:px-6 lg:px-8",
            className,
          )}
        >
          {footer}
        </div>
      )}
    </header>
  );
};

export default PageHero;
